import { Autorizacion, Objeto, Permiso, RolUsuario } from './autorizacion.models';

/*
 * Autorización necesaria para acceder a las páginas de la plataforma.
 *
 * Va con `satisfies` y no con `: Record<string, Autorizacion>` a propósito: así
 * el tipo conserva las llaves literales y una llave mal escrita —
 * `AUTORIZACION_PAGINAS['Traslados']` en vez de 'Traslados Locales'— es un error
 * de compilación. Con el `Record` era `undefined` en silencio, y como el guard
 * hace `route.data['autorizacion'] ?? {}` y una autorización vacía deja pasar a
 * cualquiera, esa ruta se quedaba sin guardián sin que nadie se enterara.
 */
export const AUTORIZACION_PAGINAS = {
  // Clientes
  Clientes: { permisos: { [Objeto.CLIENTE]: [Permiso.VER_MASIVO] } },
  'Verificaciones de Identidad': {
    permisos: {
      [Objeto.VERIFICACION_IDENTIDAD]: [Permiso.VER_MASIVO, Permiso.APROBAR],
    },
  },
  'Verificación de Identidad': {
    permisos: {
      [Objeto.VERIFICACION_IDENTIDAD]: [Permiso.VER, Permiso.APROBAR],
    },
  },
  'Ver Cliente': { permisos: { [Objeto.CLIENTE]: [Permiso.VER] } },

  // Ordenes
  Órdenes: { permisos: { [Objeto.ORDEN]: [Permiso.VER_MASIVO] } },
  'Importar Órdenes': {
    permisos: {
      [Objeto.ORDEN]: [Permiso.CREAR_MASIVO],
      [Objeto.CLIENTE]: [Permiso.VER_MASIVO],
    },
  },
  'Crear Orden': { permisos: { [Objeto.ORDEN]: [Permiso.CREAR] } },
  'Preparar Órdenes': {
    permisos: {
      [Objeto.ORDEN]: [Permiso.VER_MASIVO, Permiso.VER, Permiso.PREPARAR],
    },
  },
  'Preparar Orden': {
    permisos: {
      [Objeto.ORDEN]: [Permiso.VER, Permiso.PREPARAR],
    },
  },
  'Imprimir Etiquetas': {
    permisos: { [Objeto.ORDEN]: [Permiso.VER_MASIVO, Permiso.VER] },
  },
  'Recoger Órdenes': { permisos: { [Objeto.ORDEN]: [Permiso.RECOGER] } },
  'Retornar Órdenes': {
    permisos: {
      [Objeto.ORDEN]: [Permiso.VER_MASIVO, Permiso.VER, Permiso.RETORNAR],
    },
  },
  Recogidas: { permisos: { [Objeto.RECOGIDA]: [Permiso.VER_MASIVO] } },
  'Crear Recogida': { permisos: { [Objeto.RECOGIDA]: [Permiso.CREAR] } },
  'Ver Orden': { permisos: { [Objeto.ORDEN]: [Permiso.VER] } },

  // Domicilios
  Rutas: { permisos: { [Objeto.RUTA]: [Permiso.VER_MASIVO] } },
  'Ver Ruta': { permisos: { [Objeto.RUTA]: [Permiso.VER] } },
  Domicilios: { permisos: { [Objeto.DOMICILIO]: [Permiso.VER_MASIVO] } },
  'Enrutar Domicilios': {
    permisos: {
      [Objeto.DOMICILIO]: [Permiso.VER_MASIVO, Permiso.VER, Permiso.ENRUTAR],
    },
  },
  'Despachar Domicilios': {
    permisos: {
      [Objeto.DOMICILIO]: [Permiso.VER_MASIVO, Permiso.VER, Permiso.DESPACHAR],
    },
  },
  'Entregar Domicilios': {
    permisos: {
      [Objeto.DOMICILIO]: [Permiso.VER_MASIVO, Permiso.VER, Permiso.ENTREGAR],
    },
  },
  'Ver Domicilio': { permisos: { [Objeto.DOMICILIO]: [Permiso.VER] } },

  // Paquetes
  Paquetes: { permisos: { [Objeto.PAQUETE]: [Permiso.VER_MASIVO] } },
  'Crear Paquete': { permisos: { [Objeto.PAQUETE]: [Permiso.CREAR] } },

  // Traslados
  'Traslados Locales': {
    permisos: { [Objeto.TRASLADO]: [Permiso.VER_MASIVO] },
  },
  'Crear Traslado': {
    permisos: { [Objeto.TRASLADO]: [Permiso.CREAR] },
  },

  // Planillas
  'Planillas de Recogida': {
    permisos: { [Objeto.PLANILLA_RECOGIDA]: [Permiso.VER_MASIVO] },
  },
  'Legalizar Planillas de Recogida': {
    permisos: {
      [Objeto.PLANILLA_RECOGIDA]: [Permiso.VER_MASIVO, Permiso.VER],
      [Objeto.DOMICILIO]: [Permiso.VER_MASIVO, Permiso.VER, Permiso.LEGALIZAR],
    },
  },
  'Planillas de Rodamiento': {
    permisos: { [Objeto.PLANILLA_RODAMIENTO]: [Permiso.VER_MASIVO] },
  },
  'Ver Planilla de Rodamiento': {
    permisos: { [Objeto.PLANILLA_RODAMIENTO]: [Permiso.VER] },
  },
  'Ver Planilla de Recogida': {
    permisos: { [Objeto.PLANILLA_RECOGIDA]: [Permiso.VER] },
  },
  'Planillas de Retorno': {
    permisos: { [Objeto.PLANILLA_RETORNO]: [Permiso.VER_MASIVO] },
  },
  'Ver Planilla de Retorno': {
    permisos: { [Objeto.PLANILLA_RETORNO]: [Permiso.VER] },
  },

  // Reportes
  Reportes: { permisos: { [Objeto.REPORTE]: [Permiso.VER] } },

  // Ad Hoc
  'Tareas de Eleva': {
    permisos: { [Objeto.TAREA]: [Permiso.VER] },
    roles: [RolUsuario.ELEVA],
  },
  'Tareas de Organización': {
    permisos: { [Objeto.TAREA]: [Permiso.VER] },
    roles: [RolUsuario.FARMACIA, RolUsuario.ADMINISTRATIVO, RolUsuario.ELEVA],
  },
  'Tareas de Aliado': {
    permisos: { [Objeto.TAREA]: [Permiso.VER] },
    roles: [RolUsuario.ALIADO],
  },

  // Facturación
  'Cuentas de Cobro': {
    permisos: { [Objeto.CUENTA_COBRO]: [Permiso.VER_MASIVO] },
  },
  'Ver Cuenta de Cobro': {
    permisos: { [Objeto.CUENTA_COBRO]: [Permiso.VER] },
    roles: [RolUsuario.ELEVA],
  },

  // Trayectos
  Trayectos: { permisos: { [Objeto.REGISTRO_TRAYECTO]: [Permiso.VER_MASIVO] } },

  // Caja
  // Las dos operativas no van al menú: se entra por las tarjetas de Tareas de Eleva.
  Recepción: {
    permisos: { [Objeto.RECEPCION_EFECTIVO]: [Permiso.CREAR] },
    roles: [RolUsuario.ELEVA],
  },
  'Cerrar Caja': {
    permisos: { [Objeto.CIERRE_CAJA]: [Permiso.CREAR] },
    roles: [RolUsuario.ELEVA],
  },
  'Cajas de Auxiliares': {
    permisos: { [Objeto.CAJA_AUXILIAR]: [Permiso.VER_MASIVO] },
    roles: [RolUsuario.ELEVA],
  },
  'Ver Caja de Auxiliar': {
    permisos: { [Objeto.MOVIMIENTO_CAJA]: [Permiso.VER_MASIVO] },
    roles: [RolUsuario.ELEVA],
  },
  'Ver Cierre de Caja': {
    permisos: { [Objeto.CIERRE_CAJA]: [Permiso.VER] },
    roles: [RolUsuario.ELEVA],
  },
  'Cuentas de Pilotos': {
    permisos: { [Objeto.CUENTA_PILOTO]: [Permiso.VER_MASIVO] },
    roles: [RolUsuario.ELEVA],
  },
  'Ver Cuenta de Piloto': {
    permisos: { [Objeto.MOVIMIENTO_CUENTA]: [Permiso.VER_MASIVO] },
    roles: [RolUsuario.ELEVA],
  },

  // Campañas
  Campañas: { permisos: { [Objeto.CAMPANA]: [Permiso.VER_MASIVO] } },
  'Bases de Contactos': {
    permisos: { [Objeto.CAMPANA]: [Permiso.VER_MASIVO] },
  },

  // Servicios
  Digiturnos: { permisos: { [Objeto.DIGITURNO]: [Permiso.VER_MASIVO] } },
  'Ver Digiturno': { permisos: { [Objeto.DIGITURNO]: [Permiso.VER] } },
  'Bitácora de Encargos': {
    permisos: { [Objeto.ENCARGO]: [Permiso.VER_MASIVO] },
  },
  'Crear Encargo': { permisos: { [Objeto.ENCARGO]: [Permiso.CREAR] } },
  'Ver Encargo': { permisos: { [Objeto.ENCARGO]: [Permiso.VER] } },

  // Rastreo
  'Mapa de Pilotos': { roles: [RolUsuario.ELEVA] },
  Jornadas: { permisos: { [Objeto.JORNADA]: [Permiso.VER_MASIVO] } },

  // CRM
  Tiquetes: { permisos: { [Objeto.TIQUETE]: [Permiso.VER_MASIVO] } },
  'Crear Tiquete': { permisos: { [Objeto.TIQUETE]: [Permiso.CREAR] } },
  'Ver Tiquete': { permisos: { [Objeto.TIQUETE]: [Permiso.VER] } },
  Chats: { permisos: { [Objeto.TIQUETE]: [Permiso.VER_MASIVO] } },
  'Ver Chat': { permisos: { [Objeto.TIQUETE]: [Permiso.VER] } },
  // Conectar un número cambia a dónde llegan sus conversaciones: el backend lo reserva a
  // superusuarios (sin importar el rol)
  'WhatsApp de Asesores': { superusuario: true },
} satisfies Record<string, Autorizacion>;
